import { ChevronsUpDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

const PLACEHOLDER_EMAIL_SUFFIX = '@shopify.placeholder.invalid';

/** Sidebar footer user menu: avatar, shop name, email (or "Shop access"), and logout. */
export function NavUser() {
  const { t } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');
  const { isMobile } = useSidebar();
  const auth = useAuth();

  const session = auth.status === 'authenticated' ? auth.session : null;
  const mode = auth.status === 'authenticated' ? auth.mode : 'standalone';
  const user = session?.user ?? null;

  const name =
    session?.account?.name ??
    user?.name ??
    (mode === 'embedded' ? (session?.merchant?.shop_domain ?? 'Store') : 'Account');

  const email = user?.email ?? '';
  // Prefer the explicit backend flag; fall back to the placeholder-email pattern
  // for users whose row predates the is_shop_access column.
  const isShopAccess = user?.is_shop_access ?? email.endsWith(PLACEHOLDER_EMAIL_SUFFIX);
  const secondary = isShopAccess || !email ? t('user.shopAccess') : email;
  const initial = name.charAt(0).toUpperCase();
  // Only ever render https image sources (defence-in-depth against a future
  // write path injecting a javascript:/data: URL into the avatar).
  const avatarUrl = user?.avatar_url?.startsWith('https://') ? user.avatar_url : undefined;

  const canLogout = auth.status === 'authenticated' && mode === 'standalone';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
                <AvatarFallback className="rounded-lg bg-white/10 text-white">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium text-white">{name}</span>
                <span className="truncate text-xs text-sidebar-foreground/70">{secondary}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 opacity-70" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="size-8 rounded-lg">
                  {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
                  <AvatarFallback className="rounded-lg">{initial}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{name}</span>
                  <span className="truncate text-xs text-muted-foreground">{secondary}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            {canLogout ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void auth.logout()}>
                  <LogOut />
                  {tAuth('logout')}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
